import { Injectable } from '@angular/core';

export interface BiometricCredential {
  id: string;
  userId: string;
  companyId: string;
  publicKey: string;
  createdAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class BiometricAuthService {
  private readonly STORAGE_KEY = 'biometric_credentials';

  constructor() {}

  /**
   * 檢查瀏覽器是否支援 WebAuthn
   */
  isWebAuthnSupported(): boolean {
    return !!(navigator.credentials && navigator.credentials.create);
  }

  /**
   * 檢查是否為行動裝置
   */
  isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * 檢查是否支援生物識別
   */
  async isBiometricSupported(): Promise<boolean> {
    if (!this.isWebAuthnSupported()) {
      return false;
    }

    try {
      // 檢查是否有可用的認證器
      const available = await (navigator.credentials as any).get({
        publicKey: {
          challenge: new Uint8Array(32),
          timeout: 1000,
          userVerification: 'preferred'
        }
      }).catch(() => false);

      return true; // 即使沒有註冊的憑證，也表示支援
    } catch (error) {
      console.log('生物識別檢查失敗:', error);
      return false;
    }
  }

  /**
   * 註冊生物識別憑證
   */
  async registerBiometric(userId: string, companyId: string): Promise<boolean> {
    if (!this.isWebAuthnSupported()) {
      throw new Error('此瀏覽器不支援生物識別功能');
    }

    try {
      const challenge = this.generateChallenge();
      const userIdBuffer = new TextEncoder().encode(userId);

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: challenge,
          rp: {
            name: "JK Service Login",
            id: window.location.hostname,
          },
          user: {
            id: userIdBuffer,
            name: userId,
            displayName: userId,
          },
          pubKeyCredParams: [
            { alg: -7, type: "public-key" }, // ES256
            { alg: -257, type: "public-key" }, // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: "platform", // 使用內建認證器（如指紋、Face ID）
            userVerification: "required",
            requireResidentKey: false,
          },
          timeout: 60000,
          attestation: "direct"
        }
      }) as PublicKeyCredential;

      if (credential) {
        // 儲存憑證資訊
        const credentialData: BiometricCredential = {
          id: credential.id,
          userId: userId,
          companyId: companyId,
          publicKey: this.arrayBufferToBase64((credential.response as AuthenticatorAttestationResponse).getPublicKey()!),
          createdAt: new Date()
        };

        this.saveCredential(credentialData);
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('生物識別註冊失敗:', error);
      if (error.name === 'NotAllowedError') {
        throw new Error('使用者取消了生物識別註冊');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('此裝置不支援生物識別功能');
      } else {
        throw new Error('生物識別註冊失敗: ' + error.message);
      }
    }
  }

  /**
   * 使用生物識別進行驗證
   */
  async authenticateWithBiometric(userId: string, companyId: string): Promise<boolean> {
    if (!this.isWebAuthnSupported()) {
      throw new Error('此瀏覽器不支援生物識別功能');
    }

    const credential = this.getCredential(userId, companyId);
    if (!credential) {
      throw new Error('找不到對應的生物識別憑證，請先註冊');
    }

    try {
      const challenge = this.generateChallenge();

      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: challenge,
          allowCredentials: [{
            id: this.base64ToArrayBuffer(credential.id),
            type: 'public-key',
            transports: ['internal'] as AuthenticatorTransport[],
          }],
          userVerification: 'required',
          timeout: 60000,
        }
      }) as PublicKeyCredential;

      if (assertion) {
        // 在實際應用中，這裡應該將 assertion 發送到伺服器進行驗證
        console.log('生物識別驗證成功');
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('生物識別驗證失敗:', error);
      if (error.name === 'NotAllowedError') {
        throw new Error('使用者取消了生物識別驗證');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('生物識別憑證無效，請重新註冊');
      } else {
        throw new Error('生物識別驗證失敗: ' + error.message);
      }
    }
  }

  /**
   * 檢查使用者是否已註冊生物識別
   */
  hasBiometricCredential(userId: string, companyId: string): boolean {
    return !!this.getCredential(userId, companyId);
  }

  /**
   * 刪除生物識別憑證
   */
  removeBiometricCredential(userId: string, companyId: string): boolean {
    const credentials = this.getAllCredentials();
    const filteredCredentials = credentials.filter(
      cred => !(cred.userId === userId && cred.companyId === companyId)
    );

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredCredentials));
    return credentials.length !== filteredCredentials.length;
  }

  /**
   * 獲取所有已註冊的憑證
   */
  getAllCredentials(): BiometricCredential[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('讀取憑證失敗:', error);
      return [];
    }
  }

  /**
   * 儲存憑證
   */
  private saveCredential(credential: BiometricCredential): void {
    const credentials = this.getAllCredentials();

    // 移除舊的憑證（如果存在）
    const filteredCredentials = credentials.filter(
      cred => !(cred.userId === credential.userId && cred.companyId === credential.companyId)
    );

    filteredCredentials.push(credential);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredCredentials));
  }

  /**
   * 獲取特定使用者和公司的憑證
   */
  private getCredential(userId: string, companyId: string): BiometricCredential | null {
    const credentials = this.getAllCredentials();
    return credentials.find(cred => cred.userId === userId && cred.companyId === companyId) || null;
  }

  /**
   * 生成隨機挑戰
   */
  private generateChallenge(): BufferSource {
    const buffer = new ArrayBuffer(32);
    const challenge = new Uint8Array(buffer);
    crypto.getRandomValues(challenge);
    return challenge as BufferSource;
  }

  /**
   * ArrayBuffer 轉 Base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Base64 轉 ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
